@let isDesktop = screenDetector.isDesktop$ | async;

<cac-initialization-protection />

<mat-sidenav-container
  class="app-container z-[initial] h-full"
  [hasBackdrop]="isDesktop ? false : true"
  (backdropClick)="closeSidebar()"
>
  <!-- Parent items static SideBar -->
  <mat-sidenav
    #parent
    [opened]="isDesktop ? true : sidebarOpened()"
    [mode]="isDesktop ? 'side' : 'over'"
    class="app-sidebar bg-neutral-800"
    [style.width]="sidebarWidth()"
    [autoFocus]="false"
  >
    <div class="flex flex-col mt-4">
      <cac-button
        appearance="text"
        theme="custom"
        class="hidden lg:block -mt-3 mb-3 w-16 self-center bg-transparent text-neutral-100 hover:text-neutral-200"
        (onClick)="toggleShrink()"
      >
        <cac-icon
          icon="chevron-left"
          class="transition-all duration-100 ease-out rtl:rotate-180 {{ sidebarShrink() ? '-scale-x-100' : '' }}"
        ></cac-icon>
      </cac-button>
      @for (item of routeHelper.routes(); track item.fullPath) {
        <ng-container [ngTemplateOutlet]="SidebarItem" [ngTemplateOutletContext]="{ item }"></ng-container>
      }
    </div>
  </mat-sidenav>

  <mat-sidenav-content class="transition-all duration-500 ease-out h-full mx-0 z-[initial] lg:z-1">
    <mat-sidenav-container
      class="app-sub-container h-full z-[initial] {{ children.opened ? 'sidenav-is-open' : '' }}"
      [hasBackdrop]="true"
      (backdropClick)="closeSidenav()"
    >
      <!-- Children items expandable Sidenav -->

      <mat-sidenav
        #children
        mode="over"
        class="app-sidebar bg-neutral-variant-800"
        [style.width]="isDesktop ? '500px' : '320px'"
        [style.margin-inline-start]="sidenavOpened() && isDesktop ? sidebarWidth() : '0px'"
      >
        @if (!isDesktop) {
          <cac-icon
            class="text-neutral-50 absolute top-6 start-6"
            wrapperClass="hover:bg-neutral-900"
            size="2.5rem"
            icon="close"
            (onClick)="closeSidenav()"
          ></cac-icon>
        }
        <h1 class="text-3xl mt-4 mb-8 lg:my-5 lg:ms-12 text-white whitespace-nowrap text-center lg:text-start">
          {{ sidenavItems()?.parent?.label }}
        </h1>
        @if (sidenavItems(); as sidenavItems) {
          <div class="w-full max-h-full flex flex-col flex-wrap px-12 gap-12 pt-8">
            <ng-container
              [ngTemplateOutlet]="SidenavItems"
              [ngTemplateOutletContext]="{ items: sidenavItems.singletons }"
            ></ng-container>

            @for (group of sidenavItems.grouped; track group.label) {
              <h2 class="text-neutral-100 text-2xl -mb-8 whitespace-nowrap">
                <span class="ps-4">{{ group.label }}</span>
                <hr class="mt-6 h-px w-full border-0 bg-neutral-50 opacity-30" />
              </h2>
              <ng-container
                [ngTemplateOutlet]="SidenavItems"
                [ngTemplateOutletContext]="{ items: group.children }"
              ></ng-container>
            }
          </div>
        }
      </mat-sidenav>
      <mat-sidenav-content
        class="app-content z-[initial] mx-0"
        [style.padding-inline-start]="isDesktop ? sidebarWidth() : '0px'"
      >
        <!-- Header -->
        <ng-container [ngTemplateOutlet]="TopBar"></ng-container>

        <div class="flex flex-col relative w-full h-full overflow-hidden">
          <div class="flex-auto overflow-auto ps-2 pe-2 pt-4 pb-20 lg:ps-7 lg:pe-7 lg:pt-8 lg:pb-20">
            <router-outlet></router-outlet>
          </div>
          <ng-content></ng-content>
          <cac-loader-screen [show]="layout.loading()" [errored]="layout.failed()" [z]="9"></cac-loader-screen>
        </div>
      </mat-sidenav-content>
    </mat-sidenav-container>
  </mat-sidenav-content>
</mat-sidenav-container>

<ng-template #SoftLoader>
  <mat-progress-bar
    *ngIf="layout.softLoading()"
    class="absolute -bottom-1 left-0 right-0"
    mode="indeterminate"
  ></mat-progress-bar>
</ng-template>

<!-- button shown on top of the sidebar -->
<ng-template #NavButton let-class="class">
  <cac-icon
    (onClick)="toggleSidebar()"
    class="w-10 h-10 text-neutral-600 {{ class ?? '' }}"
    size="2rem"
    [icon]="'nav'"
  ></cac-icon>
</ng-template>

<ng-template #TopBar>
  <div
    class="app-topbar w-full flex absolute left-0 right-0 top-0 z-[9] items-center transition-all"
    [style.padding-inline-start]="isDesktop ? sidebarWidth() : '0px'"
  >
    <div class="flex w-full h-full self-center px-4 lg:px-9">
      @if (!isDesktop) {
        <ng-container [ngTemplateOutlet]="NavButton"></ng-container>
      }

      <div class="flex-auto"><!-- Spacer --></div>

      <div class="flex gap-3 items-center">
        <button [matMenuTriggerFor]="adminProfileMenu" class="profile-button">
          <img src="/assets/base/images/default-profile.webp" width="32" height="32" alt="profile" />
        </button>
      </div>

      <mat-menu #adminProfileMenu="matMenu">
        @if (isSuperAdmin()) {
          <button (click)="navigateToSettings()" mat-menu-item>Account Settings</button>
        }
        <button (click)="onLogout()" mat-menu-item>Logout</button>
      </mat-menu>

      <div class="flex items-center flex-none gap-3">
        <ng-content select="[slot=header]"></ng-content>
      </div>
    </div>
    <ng-container *ngIf="isDesktop" [ngTemplateOutlet]="SoftLoader"></ng-container>
  </div>
</ng-template>

<!-- items shown on the sidebar -->
<ng-template #SidebarItem let-item="item" let-class="class">
  @let isActive = routeHelper.isActivePartially(item.fullPath);

  @if (!item.hidden) {
    <div
      role="button"
      tabindex="0"
      (click)="navigate(item, isActive)"
      (keydown.enter)="navigate(item, isActive)"
      class="ui-sidebar-item {{ isActive ? 'active' : '' }} {{ class ?? '' }}"
    >
      @if (item.icon) {
        <mat-icon
          class="ui-sidebar-item-icon flex-none"
          [svgIcon]="item.icon.active && isActive ? item.icon.active : item.icon.default"
        ></mat-icon>
      }
      @if (item.isPlaceholder) {
        <div class="absolute top-3 bottom-3 end-1.5 bg-current text-inherit w-0.5 opacity-30"></div>
        <div
          class="transition-all absolute top-5 bottom-5 end-3 bg-current text-inherit w-0.5 {{
            sidebarShrink() ? 'opacity-0' : 'opacity-30'
          }}"
        ></div>
      }
      <span
        class="transition-all duration-500 overflow-hidden whitespace-nowrap flex flex-col items-center justify-end {{
          sidebarShrink() ? 'w-0 h-0 opacity-0' : 'w-full h-8'
        }}"
      >
        <span class="inline-flex items-center gap-2">
          {{ item.label }}
        </span>
      </span>
    </div>
  }
</ng-template>

<ng-template #SidenavItems let-items="items">
  <div class="flex flex-col gap-2">
    @for (item of items; track item.path) {
      @let isActive = routeHelper.isActivePartially(item.fullPath);

      <span
        tabindex="0"
        class="ui-sidenav-item {{ isActive ? 'active' : '' }}"
        (click)="navigate(item)"
        (keydown.enter)="navigate(item)"
      >
        <cac-icon
          [icon]="item.icon ? (item.icon.active && isActive ? item.icon.active : item.icon.default) : 'chevron-right'"
          class="text-inherit pointer-events-none {{ !item.icon ? 'rtl:rotate-180' : '' }}"
          size="1.5rem"
        ></cac-icon>

        <span class="ui-sidenav-item-label">{{ item.label }}</span>
      </span>
    }
  </div>
</ng-template>
